/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Code, Group, NumberInput, Popover, PopoverProps, Stack } from '@mantine/core'

export type AmountPercentageCalculatorPopoverProps = PopoverProps & {
  amount?: number,
  onValidate (result: number): void
  total?: number,
}

function getPercentage (amount: number, total: number) {
  return Math.round(((amount * 100) / total) * 100) / 100
}

function AmountPercentageCalculatorPopover (props: AmountPercentageCalculatorPopoverProps) {
  const {
    amount,
    children,
    onValidate,
    total,
    ...others
  } = props

  const intl = useIntl()

  const [value, setValue] = useState<number>(
    total != null && amount != null
      ? getPercentage(amount, total)
      : null)

  useEffect(() => {
    if (total != null && amount != null) {
      setValue(getPercentage(amount, total))
    }
  }, [amount, total])

  const percentage = useMemo(() => value != null ? value : null, [value])

  const result = useMemo(() => (
    percentage != null ? total * (percentage / 100) : null
  ), [percentage, total])

  const handleInputChange = useCallback((value: number) => {
    setValue(value)
  }, [])

  const handleSubmit = useCallback(() => {
    onValidate(total * (percentage / 100))
  }, [onValidate, percentage, total])

  const handleInputKeyUp = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }, [handleSubmit])

  return (
    <Popover
      closeOnClickOutside
      trapFocus
      {...others}
    >
      <Popover.Target>
        {children}
      </Popover.Target>

      <Popover.Dropdown>
        <Stack>
          <Group>
            <NumberInput
              allowDecimal
              thousandSeparator
              onChange={handleInputChange}
              onKeyUp={handleInputKeyUp}
              step={0.01}
              suffix="%"
              value={value != null ? value : ''}
            />
            <Button
              onClick={handleSubmit}
              size="xs"
            >
              {intl.formatMessage({
                id: 'ok',
                defaultMessage: 'OK'
              })}
            </Button>
          </Group>
          <Code p="sm">
            {`${intl.formatNumber(total)} x ${intl.formatNumber(value)}% = ${result != null ? intl.formatNumber(result) : '?'}`}
          </Code>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

export default AmountPercentageCalculatorPopover
